import { Form } from '@/components';
import { PricingMarkupDefaultsDto, SaleTaxApplyTo } from '@/core';
import { UpdatePricingMarkupDefaultsArgs } from '@/redux';
import { Card, Col, Divider, Row, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface Props {
  value: UpdatePricingMarkupDefaultsArgs;
  defaultMarkups?: PricingMarkupDefaultsDto;
  initialMarkups?: PricingMarkupDefaultsDto;
  onSubmit: (value: UpdatePricingMarkupDefaultsArgs) => any;
  autoSubmit: boolean;
}

const SCHEMA = Yup.object().shape({
  offeredItemConstructionMarkup: Yup.number().min(0).max(10000).nullable(),
  offeredItemResidentMarkup: Yup.number().min(0).max(10000).nullable(),
  specialRequestConstructionMarkup: Yup.number().min(0).max(10000).nullable(),
  specialRequestResidentMarkup: Yup.number().min(0).max(10000).nullable(),
  salesTax: Yup.number().min(0).max(10000).nullable(),
  applyTo: Yup.array()
    .of(Yup.mixed<SaleTaxApplyTo>().nullable().required())
    .nullable(),
});

function useHandleSubmit(props: Props) {
  const { value, onSubmit } = props;
  return useCallback(
    (formValue: UpdatePricingMarkupDefaultsArgs) => {
      if (
        formValue.offeredItemConstructionMarkup !==
          value.offeredItemConstructionMarkup ||
        formValue.offeredItemResidentMarkup !==
          value.offeredItemResidentMarkup ||
        formValue.specialRequestConstructionMarkup !==
          value.specialRequestConstructionMarkup ||
        formValue.specialRequestResidentMarkup !==
          value.specialRequestResidentMarkup ||
        formValue.salesTax !== value.salesTax ||
        formValue.applyTo !== value.applyTo
      ) {
        return onSubmit(formValue);
      }
    },
    [value, onSubmit],
  );
}

export function PricingSettingsDefaultsForm(props: Props) {
  const { value, defaultMarkups, initialMarkups } = props;
  const onSubmit = useHandleSubmit(props);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'pricing.settings.defaults',
  });
  return (
    <Form.Formik<UpdatePricingMarkupDefaultsArgs>
      initialValues={{ ...value, applyTo: value.applyTo ?? [] }}
      onSubmit={onSubmit}
      uid="pricing-settings-defaults"
      i18n="pricing.settings.defaults"
      validationSchema={SCHEMA}
      enableReinitialize
    >
      {(formik) => (
        <>
          <Col span={24} flex="auto">
            <Row className="mb-2">
              <Col span={12}>
                <Card>
                  <Typography.Title level={5}>
                    {t('offeredItem')}
                  </Typography.Title>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Number
                        name="offeredItemConstructionMarkup"
                        prefix="%"
                        min={0}
                        max={10000}
                        precision={6}
                        onBlur={formik.submitForm}
                        placeholder={(
                          defaultMarkups ?? initialMarkups
                        )?.offeredItemConstruction?.value.toFixed(6)}
                        allowClearValue
                      />
                    </Col>

                    <Col span={12}>
                      <Form.Number
                        name="offeredItemResidentMarkup"
                        prefix="%"
                        min={0}
                        max={10000}
                        precision={6}
                        onBlur={formik.submitForm}
                        placeholder={(
                          defaultMarkups ?? initialMarkups
                        )?.offeredItemResident?.value.toFixed(6)}
                        allowClearValue
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col span={12}>
                <Card>
                  <Typography.Title level={5}>
                    {t('specialRequest')}
                  </Typography.Title>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Number
                        name="specialRequestConstructionMarkup"
                        prefix="%"
                        min={0}
                        max={10000}
                        precision={6}
                        onBlur={formik.submitForm}
                        placeholder={(
                          defaultMarkups ?? initialMarkups
                        )?.specialRequestConstruction?.value.toFixed(6)}
                        allowClearValue
                      />
                    </Col>

                    <Col span={12}>
                      <Form.Number
                        name="specialRequestResidentMarkup"
                        prefix="%"
                        min={0}
                        max={10000}
                        precision={6}
                        onBlur={formik.submitForm}
                        placeholder={(
                          defaultMarkups ?? initialMarkups
                        )?.specialRequestResident?.value.toFixed(6)}
                        allowClearValue
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Divider />
            <Row>
              <Col span={12}>
                <Card bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Number
                        name="salesTax"
                        prefix="%"
                        min={0}
                        max={10000}
                        precision={6}
                        onBlur={formik.submitForm}
                        placeholder={(
                          defaultMarkups ?? initialMarkups
                        )?.salesTax?.value.toFixed(6)}
                        allowClearValue
                      />
                    </Col>
                    <Col span={12}>
                      <Form.EnumSelect
                        name="applyTo"
                        type={SaleTaxApplyTo}
                        mode="multiple"
                        onBlur={formik.submitForm}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Col>
        </>
      )}
    </Form.Formik>
  );
}
